import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, TrendingUp } from "lucide-react";

import { trackEvent } from "@/lib/trackEvent";

export interface RelatedArticle {
  id: string;
  slug: string;
  headline: string;
  category?: string | null;
  og_image_url?: string | null;
}

interface Props {
  related: RelatedArticle[];
  coins: string[];
  articleId?: string;
  slug?: string;
}

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600&h=400&fit=crop&auto=format";

export default function ArticleFooterCTA({ related, coins, articleId, slug }: Props) {
  return (
    <section className="space-y-12 py-12 border-t border-border/50">
      {/* Continue reading */}
      {related.length > 0 && (
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold mb-6">Continue reading</h3>
          <div className="grid gap-5 sm:grid-cols-3">
            {related.slice(0, 3).map((r) => (
              <Link
                key={r.id}
                to={`/articles/${r.slug}`}
                onClick={() =>
                  trackEvent("related_article_clicked", {
                    from_article_id: articleId,
                    from_slug: slug,
                    to_slug: r.slug,
                  })
                }
                className="group block"
              >
                <Card className="overflow-hidden h-full border-border/60 hover:border-primary/50 transition-colors">
                  <img
                    src={r.og_image_url || FALLBACK_IMAGE}
                    alt={r.headline}
                    loading="lazy"
                    className="w-full h-36 object-cover"
                  />
                  <div className="p-4">
                    {r.category && (
                      <Badge variant="outline" className="mb-2 text-xs border-primary/40 text-primary">
                        {r.category}
                      </Badge>
                    )}
                    <p className="font-semibold leading-snug text-base group-hover:text-primary transition-colors line-clamp-3">
                      {r.headline}
                    </p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Analyze the coins */}
      <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 sm:p-8">
        <h3 className="text-2xl sm:text-3xl font-bold mb-2">Analyze the coins in this article</h3>
        <p className="text-muted-foreground mb-5 text-base">
          Get an instant AI review — fundamentals, risk and verdict.
        </p>
        <div className="flex flex-wrap gap-3">
          {(coins.length > 0 ? coins : ["BTC", "ETH", "SOL"]).slice(0, 6).map((coin) => (
            <Button
              key={coin}
              asChild
              size="lg"
              variant="secondary"
              onClick={() =>
                trackEvent("cta_clicked_to_coin_reviewer", {
                  placement: "footer_coin_button",
                  coin,
                  article_id: articleId,
                  slug,
                })
              }
            >
              <Link to={`/coin-reviewer?coin=${encodeURIComponent(coin)}`}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Review {coin}
              </Link>
            </Button>
          ))}
          <Button
            asChild
            size="lg"
            onClick={() =>
              trackEvent("cta_clicked_to_coin_reviewer", {
                placement: "footer_any_coin",
                article_id: articleId,
                slug,
              })
            }
          >
            <Link to="/coin-reviewer">
              Review any coin
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>

    </section>

  );
}
